import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

// Admin-only features cannot be granted to non-admins — excluded from this list
const FEATURES = [
  { key: 'dashboard',      label: 'Dashboard',          icon: '📊' },
  { key: 'employees',      label: 'Employees',           icon: '👔' },
  { key: 'visitors',       label: 'Visitors',            icon: '👥' },
  { key: 'meetings',       label: 'Meetings',            icon: '📅' },
  { key: 'meeting_rooms',  label: 'Meeting Room Setup',  icon: '🚪' },
  { key: 'guest_house',    label: 'Guest House & Rooms', icon: '🏠' },
  { key: 'hotels',         label: 'Hotel Bookings',      icon: '🏩' },
  { key: 'couriers',       label: 'Couriers',            icon: '📦' },
  { key: 'vehicles',       label: 'Vehicles',            icon: '🚙' },
  { key: 'expenses',       label: 'Expenses',            icon: '💰' },
  { key: 'reports',        label: 'Reports',             icon: '📈' },
  { key: 'address_book',   label: 'Address Book',        icon: '📒' },
  { key: 'users',          label: 'User Management',     icon: '⚙️'  },
];

const ROLE_COLORS = {
  admin:         'bg-red-100 text-red-700',
  manager:       'bg-purple-100 text-purple-700',
  receptionist:  'bg-blue-100 text-blue-700',
  boss_secretary:'bg-orange-100 text-orange-700',
  general_user:  'bg-gray-100 text-gray-600',
};

export default function AccessManagement() {
  const { user: currentUser } = useAuth();

  const [users, setUsers]               = useState([]);
  const [companies, setCompanies]       = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions]   = useState({});   // { feature: { read, write, hidden } }
  const [companyAccess, setCompanyAccess] = useState({}); // { companyId: { read, write } }
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerms,  setSavingPerms]  = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const [userSearch,   setUserSearch]   = useState('');
  const [toast, setToast]               = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = async () => {
    if (!currentUser) return;
    setLoadingUsers(true);
    try { const r = await api.get('/api/users'); setUsers(r.data); } catch { setUsers([]); }
    try { const r = await api.get('/api/companies'); setCompanies(r.data); } catch { setCompanies([]); }
    setLoadingUsers(false);
  };

  useEffect(() => { if (currentUser) load(); }, [currentUser?.role]);

  const loadUserPermissions = async (userId) => {
    setLoadingPerms(true);
    setPermissions({});
    setCompanyAccess({});

    // --- Feature Permissions ---
    try {
      const r = await api.get(`/api/permissions/users/${userId}`);
      const permData = r.data || [];
      const permMap = {};
      FEATURES.forEach(f => { permMap[f.key] = { read: true, write: true, hidden: false }; });
      permData.forEach(p => {
        if (permMap[p.feature] !== undefined) {
          permMap[p.feature] = {
            read:   p.can_read  !== false,
            write:  p.can_write !== false,
            hidden: !!p.is_hidden,           // ← correct field name
          };
        }
      });
      setPermissions(permMap);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      const permMap = {};
      FEATURES.forEach(f => { permMap[f.key] = { read: true, write: true, hidden: false }; });
      setPermissions(permMap);
    }

    // --- Company Access --- (separate try-catch so failure doesn't affect permissions)
    try {
      const r = await api.get(`/api/permissions/company-access/users/${userId}`);
      const accessData = r.data || [];
      // Build fresh map with all companies = false
      const accessMap = {};
      companies.forEach(c => { accessMap[c.id] = { read: false, write: false }; });
      accessData.forEach(a => {
        if (accessMap[a.company_id] !== undefined) {
          accessMap[a.company_id] = {
            read:  !!a.read_access,   // ← correct field name (was a.can_read)
            write: !!a.write_access,  // ← correct field name (was a.can_write)
          };
        }
      });
      setCompanyAccess(accessMap);
    } catch (err) {
      console.error('Failed to load company access:', err);
      const accessMap = {};
      companies.forEach(c => { accessMap[c.id] = { read: false, write: false }; });
      setCompanyAccess(accessMap);
    }

    setLoadingPerms(false);
  };

  const selectUser = (u) => {
    setSelectedUser(u);
    loadUserPermissions(u.id);
  };

  const togglePerm = (feature, field) => {
    setPermissions(prev => ({
      ...prev,
      [feature]: { ...prev[feature], [field]: !prev[feature]?.[field] },
    }));
  };

  const setAllPerms = (read, write) => {
    const permMap = {};
    FEATURES.forEach(f => { permMap[f.key] = { read, write, hidden: false }; });
    setPermissions(permMap);
  };

  const toggleAccess = (companyId, field) => {
    setCompanyAccess(prev => ({
      ...prev,
      [companyId]: { ...prev[companyId], [field]: !prev[companyId]?.[field] },
    }));
  };

  const savePermissions = async () => {
    if (!selectedUser) return;
    setSavingPerms(true);
    try {
      const permList = FEATURES.map(f => ({
        feature:   f.key,
        can_read:  permissions[f.key]?.read  !== false,
        can_write: permissions[f.key]?.write !== false,
        is_hidden: !!permissions[f.key]?.hidden,   // ← correct field name (was `hidden`)
      }));
      await api.put(`/api/permissions/users/${selectedUser.id}`, { permissions: permList });
      showToast('✅ Permissions saved — user must re-login to see sidebar changes');
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || 'Failed to save permissions'));
    } finally {
      setSavingPerms(false);
    }
  };

  const saveCompanyAccess = async () => {
    if (!selectedUser) return;
    setSavingAccess(true);
    try {
      const accessList = companies.map(c => ({
        company_id:   c.id,
        read_access:  !!companyAccess[c.id]?.read,   // ← correct field name (was can_read)
        write_access: !!companyAccess[c.id]?.write,  // ← correct field name (was can_write)
      }));
      await api.put(`/api/permissions/company-access/users/${selectedUser.id}`, { access: accessList });
      showToast('✅ Company access saved');
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || 'Failed to save company access'));
    } finally {
      setSavingAccess(false);
    }
  };

  const filteredUsers = users.filter(u =>
    (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.username  || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Access Management</h1>
        <p className="text-gray-500 text-sm">Control what each user can see and do</p>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700 space-y-1">
        <p><strong>READ</strong> — user can see the page &nbsp;|&nbsp; <strong>WRITE</strong> — user can add/edit/delete &nbsp;|&nbsp; <strong>HIDDEN</strong> — removes from sidebar entirely</p>
        <p className="text-blue-500 text-xs">⚠️ Changes take effect after the user logs out and logs back in. Companies / Units / Backup are always Admin-only and cannot be changed here.</p>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${toast.startsWith('✅') ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast}
        </div>
      )}

      <div className="flex gap-5">
        {/* Left — user list */}
        <div className="w-64 flex-shrink-0 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col" style={{maxHeight:'75vh'}}>
          <div className="p-3 border-b">
            <input type="text" placeholder="Search users..."
              value={userSearch} onChange={e => setUserSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="overflow-y-auto flex-1">
            {loadingUsers ? (
              <div className="p-4 text-sm text-gray-400 text-center">Loading...</div>
            ) : filteredUsers.map(u => (
              <button key={u.id} onClick={() => selectUser(u)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-blue-50 transition-colors ${selectedUser?.id === u.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}>
                <div className="font-medium text-sm text-gray-800 truncate">{u.full_name || u.username}</div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                    {u.role}
                  </span>
                  {u.company_name && <span className="text-xs text-gray-400 truncate">{u.company_name}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right — permission matrix */}
        <div className="flex-1 space-y-5 min-w-0">
          {!selectedUser ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
              <div className="text-5xl mb-3">👈</div>
              <p className="text-gray-500 font-medium">Select a user from the left panel</p>
            </div>
          ) : (
            <>
              {/* User info bar */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-800">{selectedUser.full_name || selectedUser.username}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[selectedUser.role] || 'bg-gray-100 text-gray-600'}`}>
                      {selectedUser.role}
                    </span>
                    {selectedUser.company_name && <span className="text-xs text-gray-500">{selectedUser.company_name}</span>}
                  </div>
                </div>
                <div className="text-xs text-gray-400">ID: {selectedUser.id}</div>
              </div>

              {/* Feature permissions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">Feature Permissions</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Hidden = removed from sidebar</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setAllPerms(true, true)}
                      className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">✅ All On</button>
                    <button onClick={() => setAllPerms(false, false)}
                      className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">❌ All Off</button>
                    <button onClick={savePermissions} disabled={savingPerms || loadingPerms}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                      {savingPerms ? 'Saving...' : 'Save Permissions'}
                    </button>
                  </div>
                </div>
                {loadingPerms ? (
                  <div className="p-8 text-center text-gray-400">Loading...</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Feature</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-20">Read</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-20">Write</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-red-400 uppercase w-24">Hidden</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {FEATURES.map(f => (
                        <tr key={f.key} className={`hover:bg-gray-50 ${permissions[f.key]?.hidden ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-700">
                            {f.icon} {f.label}
                            {permissions[f.key]?.hidden && <span className="ml-2 text-xs text-red-500 font-normal">hidden from sidebar</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input type="checkbox"
                              checked={permissions[f.key]?.read !== false}
                              onChange={() => togglePerm(f.key, 'read')}
                              disabled={permissions[f.key]?.hidden}
                              className="w-4 h-4 text-blue-600 rounded cursor-pointer" />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input type="checkbox"
                              checked={permissions[f.key]?.write !== false}
                              onChange={() => togglePerm(f.key, 'write')}
                              disabled={permissions[f.key]?.hidden}
                              className="w-4 h-4 text-blue-600 rounded cursor-pointer" />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input type="checkbox"
                              checked={!!permissions[f.key]?.hidden}
                              onChange={() => togglePerm(f.key, 'hidden')}
                              className="w-4 h-4 text-red-500 rounded cursor-pointer" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Company Access — super admin scope only */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">Company Access</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Grant this user cross-company read/write — only applicable to <strong>admin</strong> role.
                      Has no effect on regular users (they are always scoped to their own company).
                    </p>
                  </div>
                  <button onClick={saveCompanyAccess} disabled={savingAccess || loadingPerms}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                    {savingAccess ? 'Saving...' : 'Save Access'}
                  </button>
                </div>
                {selectedUser.role !== 'admin' && (
                  <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
                    ⚠️ This user is <strong>{selectedUser.role}</strong> — company access only applies to admin-role users. Non-admin users are always restricted to their assigned company.
                  </div>
                )}
                {loadingPerms ? (
                  <div className="p-8 text-center text-gray-400">Loading...</div>
                ) : companies.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">No companies found</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Company</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-20">Read</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-20">Write</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {companies.map(c => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-700">{c.name}</td>
                          <td className="px-4 py-3 text-center">
                            <input type="checkbox"
                              checked={!!companyAccess[c.id]?.read}
                              onChange={() => toggleAccess(c.id, 'read')}
                              disabled={selectedUser.role !== 'admin'}
                              className="w-4 h-4 text-blue-600 rounded cursor-pointer disabled:opacity-40" />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input type="checkbox"
                              checked={!!companyAccess[c.id]?.write}
                              onChange={() => toggleAccess(c.id, 'write')}
                              disabled={selectedUser.role !== 'admin'}
                              className="w-4 h-4 text-blue-600 rounded cursor-pointer disabled:opacity-40" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
